/**
 * Benchmark Service
 * 
 * Service for fetching facility benchmarking data from the BI Reports API.
 * Allows facilities to compare their performance against anonymized industry averages.
 */

import axios from 'axios';
import { getAuthToken } from '../utils/authUtils';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Get facility benchmark comparison against industry averages
 * @param {string} facilityId - The facility ID to get benchmarks for
 * @param {string} category - Optional category filter ('all', 'patient_volume', 'clinical_operations', etc.)
 * @returns {Promise<Object>} Benchmark comparison data
 */
export async function getFacilityBenchmarks(facilityId, category = 'all') {
  try {
    console.log('Getting facility benchmarks:', facilityId, category);
    const token = await getAuthToken();
    
    if (!facilityId) {
      throw new Error('Facility ID is required');
    }
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    if (category && category !== 'all') {
      params.append('category', category);
    }
    
    const response = await axios.get(
      `${API_BASE}/bi/benchmarks/facility-comparison?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting facility benchmarks:', error);
    throw error;
  }
}

/**
 * Get industry benchmark summary (aggregate statistics, no facility-specific data)
 * @param {string} category - Optional category filter
 * @returns {Promise<Object>} Industry summary data
 */
export async function getIndustryBenchmarkSummary(category = 'all') {
  try {
    console.log('Getting industry benchmark summary:', category);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    if (category && category !== 'all') {
      params.append('category', category);
    }
    
    const response = await axios.get(
      `${API_BASE}/bi/benchmarks/industry-summary?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting industry summary:', error);
    throw error;
  }
}

/**
 * Get benchmark trends over time for a facility
 * @param {string} facilityId - The facility ID
 * @param {string} metricId - Optional specific metric to get trends for
 * @param {number} days - Number of days to look back (default 30)
 * @returns {Promise<Object>} Trend data
 */
export async function getBenchmarkTrends(facilityId, metricId = null, days = 30) {
  try {
    console.log('Getting benchmark trends:', facilityId, metricId, days);
    const token = await getAuthToken();
    
    if (!facilityId) {
      throw new Error('Facility ID is required');
    }
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    params.append('days', days.toString());
    if (metricId) {
      params.append('metricId', metricId);
    }
    
    const response = await axios.get(
      `${API_BASE}/bi/benchmarks/trends?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting benchmark trends:', error);
    throw error;
  }
}

/**
 * Get available benchmark categories
 * @returns {Promise<Object>} Available categories and their metrics
 */
export async function getBenchmarkCategories() {
  try {
    console.log('Getting benchmark categories');
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/bi/benchmarks/categories`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting benchmark categories:', error);
    throw error;
  }
}

/**
 * Trigger manual benchmark calculation (admin only)
 * @returns {Promise<Object>} Calculation result
 */
export async function triggerBenchmarkCalculation() {
  try {
    console.log('Triggering benchmark calculation');
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/bi/benchmarks/calculate`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error triggering benchmark calculation:', error);
    throw error;
  }
}

/**
 * Helper function to get performance tier color
 * @param {string} tier - Performance tier
 * @returns {string} Color code
 */
export function getPerformanceTierColor(tier) {
  switch (tier) {
    case 'Top Performer':
      return '#4caf50'; // Green
    case 'Above Average':
      return '#8bc34a'; // Light green
    case 'Below Average':
      return '#ff9800'; // Orange
    case 'Needs Improvement':
      return '#f44336'; // Red
    default:
      return '#9e9e9e'; // Grey
  }
}

/**
 * Helper function to get variance color (positive = green, negative = red)
 * @param {number} variance - Variance value
 * @param {boolean} higherIsBetter - Whether higher values are better (default true)
 * @returns {string} Color code
 */
export function getVarianceColor(variance, higherIsBetter = true) {
  if (variance === null || variance === undefined) return '#9e9e9e';
  
  const isPositive = higherIsBetter ? variance > 0 : variance < 0;
  const isNeutral = Math.abs(variance) < 0.5;
  
  if (isNeutral) return '#9e9e9e'; // Grey for neutral
  return isPositive ? '#4caf50' : '#f44336'; // Green or Red
}

/**
 * Helper function to format metric value with appropriate suffix
 * @param {number} value - The value to format
 * @param {string} metricId - The metric identifier
 * @returns {string} Formatted value
 */
export function formatBenchmarkValue(value, metricId) {
  if (value === null || value === undefined) return 'N/A';
  
  // Metrics that are percentages
  const percentageMetrics = [
    'active_patient_rate',
    'encounter_completion_rate',
    'no_show_rate',
    'appointment_show_rate',
    'cancellation_rate',
    'collection_rate',
    'quality_target_achievement'
  ];
  
  if (percentageMetrics.includes(metricId)) {
    return `${value.toFixed(1)}%`;
  }
  
  // Metrics that are counts
  if (metricId === 'total_patients' || metricId === 'encounters_per_day') {
    return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
  }
  
  return value.toFixed(2);
}

/**
 * Helper function to determine if higher is better for a metric
 * @param {string} metricId - The metric identifier
 * @returns {boolean} True if higher values are better
 */
export function isHigherBetter(metricId) {
  // Metrics where LOWER is better
  const lowerIsBetterMetrics = [
    'no_show_rate',
    'cancellation_rate'
  ];
  
  return !lowerIsBetterMetrics.includes(metricId);
}

const benchmarkService = {
  getFacilityBenchmarks,
  getIndustryBenchmarkSummary,
  getBenchmarkTrends,
  getBenchmarkCategories,
  triggerBenchmarkCalculation,
  getPerformanceTierColor,
  getVarianceColor,
  formatBenchmarkValue,
  isHigherBetter
};

export default benchmarkService;

